import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { callWithAuthRetry } from '../services/auth.service';

const MAX_AUTH_ERROR_RETRIES = 5;
const AUTH_ERROR_RETRY_DELAY_MS = 3000;
const PERMISSIONS_CACHE_KEY = 'onyx.permissions.cache.v1';
const PERMISSIONS_CACHE_TTL_MS = 5 * 60 * 1000;

const emptyPermissionsState = {
  permissions: [],
  roles: [],
  userType: null,
  roleTypes: [],
  orgId: null,
  orgName: null,
  workspaceId: null,
  participantScope: 'all',
  isPlatformAdmin: false,
  loading: true,
  error: null
};

const readPermissionsCache = () => {
  try {
    const raw = sessionStorage.getItem(PERMISSIONS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt || !parsed?.state) return null;
    if (Date.now() - parsed.savedAt > PERMISSIONS_CACHE_TTL_MS) return null;
    return parsed.state;
  } catch {
    return null;
  }
};

const writePermissionsCache = (state) => {
  try {
    const { loading, error, ...cacheable } = state;
    sessionStorage.setItem(
      PERMISSIONS_CACHE_KEY,
      JSON.stringify({ savedAt: Date.now(), state: cacheable }),
    );
  } catch {
    // sessionStorage may be unavailable; ignore
  }
};

const clearPermissionsCache = () => {
  try {
    sessionStorage.removeItem(PERMISSIONS_CACHE_KEY);
  } catch {
    // ignore
  }
};

const PermissionsContext = createContext({
  ...emptyPermissionsState,
});

export const PermissionsProvider = ({ children }) => {
  const { isAuthenticated, getAccessTokenSilently, isLoading: authLoading } = useAuth0();
  const cachedOnMountRef = useRef(null);
  if (cachedOnMountRef.current === null) {
    cachedOnMountRef.current = readPermissionsCache() || false;
  }
  const [state, setState] = useState(() => {
    const cached = cachedOnMountRef.current;
    if (!cached) return { ...emptyPermissionsState };
    return { ...cached, loading: false, error: null };
  });
  const retryCountRef = useRef(0);
  const authErrorRetryRef = useRef(0);

  useEffect(() => {
    if (authLoading) {
      return; // Wait for Auth0 to finish loading
    }
    
    if (isAuthenticated) {
      retryCountRef.current = 0;
      authErrorRetryRef.current = 0;
      // Stale-while-revalidate: keep cached permissions for route guards, refresh in background.
      loadPermissions({ keepShowingCache: Boolean(cachedOnMountRef.current) });
    } else {
      clearPermissionsCache();
      cachedOnMountRef.current = false;
      setState({
        ...emptyPermissionsState,
        loading: false,
      });
    }
  }, [isAuthenticated, authLoading]);

  const resolveUserType = (roleTypes, isPlatformAdmin, serverUserType) => {
    // Trust the server's user_type determination
    // Server already handles the logic of checking admin role + admin permissions
    if (serverUserType) {
      return serverUserType;
    }

    // Fallback: Check roleTypes if server didn't provide user_type
    if (isPlatformAdmin) {
      return 'admin';
    }

    if (Array.isArray(roleTypes)) {
      if (roleTypes.includes('admin')) {
        return 'admin';
      }
      if (roleTypes.includes('customer')) {
        return 'customer';
      }
      if (roleTypes.includes('member')) {
        return 'member';
      }
    }

    return null;
  };

  const loadPermissions = async ({ keepShowingCache = false } = {}) => {
    // Set loading to true BEFORE starting async fetch to prevent race condition,
    // unless we already have a warm sessionStorage cache for route guards.
    if (!keepShowingCache) {
      setState(prev => ({ ...prev, loading: true }));
    }
    
    try {
      // Use callWithAuthRetry to handle the race condition where Post-User-Registration
      // Action hasn't completed yet (user just signed up, token missing org context)
      const { data, error } = await callWithAuthRetry(
        getAccessTokenSilently,
        async (token) => {
          const response = await fetch(
            `${process.env.REACT_APP_API_SERVER_URL}/api/auth/me/permissions/`,
            {
              headers: { 'Authorization': `Bearer ${token}` }
            }
          );
          
          if (!response.ok) {
            // Include status code in error for retry logic to detect 403
            return { 
              data: null, 
              error: { message: `HTTP ${response.status}: Failed to load permissions`, status: response.status } 
            };
          }
          
          const jsonData = await response.json();
          return { data: jsonData, error: null };
        }
      );
      
      if (error) {
        const isAuthRelated = error.status === 401 || error.status === 403;
        if (isAuthRelated && authErrorRetryRef.current < MAX_AUTH_ERROR_RETRIES) {
          authErrorRetryRef.current += 1;
          console.log(`⏳ Permissions auth error (${error.status}), retrying in ${AUTH_ERROR_RETRY_DELAY_MS}ms with fresh token (attempt ${authErrorRetryRef.current}/${MAX_AUTH_ERROR_RETRIES})...`);
          setTimeout(() => {
            loadPermissions({ keepShowingCache });
          }, AUTH_ERROR_RETRY_DELAY_MS);
          return;
        }
        console.error('Failed to load permissions:', error);
        setState(prev => ({ ...prev, loading: false, error: 'Failed to load permissions' }));
        return;
      }
      
      const roleTypes = data.role_types || [];
      const permissions = data.permissions || [];
      
      // Additional retry for empty permissions (Post-User-Registration may still be processing)
      if (permissions.length === 0 && data.roles?.length === 0 && retryCountRef.current < 3) {
        retryCountRef.current += 1;
        console.log(`⏳ Permissions empty after registration, retrying in 2s with fresh token (attempt ${retryCountRef.current}/3)...`);
        setTimeout(() => {
          loadPermissions({ keepShowingCache });
        }, 2000);
        return; // Don't update state yet, keep loading
      }
      
      retryCountRef.current = 0;
      authErrorRetryRef.current = 0;
      
      const nextState = {
        permissions,
        roles: data.roles || [],
        userType: resolveUserType(roleTypes, data.is_platform_admin, data.user_type),
        roleTypes,
        orgId: data.org_id,
        orgName: data.org_name || null,
        workspaceId: data.workspace_id,
        participantScope: data.participant_scope || 'all',
        isPlatformAdmin: data.is_platform_admin || false,
        loading: false,
        error: null
      };
      writePermissionsCache(nextState);
      cachedOnMountRef.current = nextState;
      setState(nextState);
    } catch (err) {
      const errStatus = err.response?.status || err.status;
      const isAuthRelated = errStatus === 401 || errStatus === 403;
      if (isAuthRelated && authErrorRetryRef.current < MAX_AUTH_ERROR_RETRIES) {
        authErrorRetryRef.current += 1;
        console.log(`⏳ Permissions exception (${errStatus}), retrying in ${AUTH_ERROR_RETRY_DELAY_MS}ms (attempt ${authErrorRetryRef.current}/${MAX_AUTH_ERROR_RETRIES})...`);
        setTimeout(() => {
          loadPermissions({ keepShowingCache });
        }, AUTH_ERROR_RETRY_DELAY_MS);
        return;
      }
      console.error('Failed to load permissions:', err);
      setState(prev => ({ ...prev, loading: false, error: err.message }));
    }
  };

  return (
    <PermissionsContext.Provider value={state}>
      {children}
    </PermissionsContext.Provider>
  );
};

export const usePermissions = () => useContext(PermissionsContext);

// Hook for permission checks
export const usePermission = () => {
  const { permissions, isPlatformAdmin, loading } = usePermissions();
  
  return {
    // Platform admin bypass: admins can do everything
    can: (permission) => !loading && (isPlatformAdmin || permissions.includes(permission)),
    canAny: (perms) => !loading && (isPlatformAdmin || (Array.isArray(perms) && perms.some(p => permissions.includes(p)))),
    canAll: (perms) => !loading && (isPlatformAdmin || (Array.isArray(perms) && perms.every(p => permissions.includes(p)))),
    isPlatformAdmin: !loading && isPlatformAdmin,
    loading
  };
};

// Hook for role checks
export const useRole = () => {
  const { roles, roleTypes, userType, isPlatformAdmin, permissions, loading } = usePermissions();
  const resolvedRoleTypes = Array.isArray(roleTypes) && roleTypes.length > 0
    ? roleTypes
    : (userType ? [userType] : []);
  
  // Trust the server's userType determination
  // Server already checks for admin role + admin:* permissions
  const isAdminUX = userType === 'admin';
  
  const isSiteCoordinator = !loading && Array.isArray(roles) && roles.includes('site_coordinator');
  const isPhlebotomist = !loading && (
    (Array.isArray(roles) && roles.includes('phlebotomist'))
    || permissions.includes('clinic:phleb_protocol_run')
  );
  // `isClinic` follows the server's `userType === 'clinic'` decision so it
  // mirrors the same precedence as the backend (admin > customer > clinic >
  // member). `isClinicStaff` is the role-membership check (does the user
  // *have* site_coordinator or phlebotomist?), which is the right gate for
  // showing CSC/phleb-specific UI inside the clinic dashboard.
  return {
    hasRole: (role) => !loading && roles.includes(role),
    hasAnyRole: (roleList) => !loading && roleList.some(r => roles.includes(r)),
    isMember: !loading && userType === 'member',
    isCustomer: !loading && userType === 'customer',
    isClinic: !loading && userType === 'clinic',
    isAdmin: !loading && isAdminUX,
    isPlatformAdmin: !loading && isPlatformAdmin,  // Only true for 'admin' role (from server)
    isSiteCoordinator,
    isPhlebotomist,
    isClinicStaff: isSiteCoordinator || isPhlebotomist,
    userType,
    loading
  };
};
